import { useState } from 'react'
import { App as AntdApp, Button, Dropdown, Form, Input, Modal, Popconfirm, Space, Switch, Tooltip } from 'antd'
import { DeleteOutlined, DownOutlined, SaveOutlined, StarFilled, TeamOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import apiClient from '../api/client'

// SavedViews — benannte Filter-Voreinstellungen für eine Listenseite.
//
// Wiederkehrende Auswertungen ("Überfällig & SEPA", "Verträge ohne
// Provisioning") mussten bisher jedes Mal neu zusammengeklickt werden.
//
// Bewusst generisch: Die Seite übergibt ihr eigenes Filterobjekt und eine
// onApply-Funktion; die Komponente kennt den Inhalt nicht und reicht ihn
// unverändert durch. Dadurch lässt sie sich ohne Backend-Änderung an jede
// weitere Liste hängen — nur der pageKey muss eindeutig sein.

export interface SavedView<T> {
  id: number
  name: string
  params: T
  shared: boolean
  own: boolean
  owner_email: string
}

export default function SavedViews<T extends object>({
  pageKey,
  current,
  onApply,
  isEmpty,
}: {
  /** Eindeutiger Schlüssel der Liste, z. B. 'invoices'. */
  pageKey: string
  /** Aktuelle Filterwerte der Seite — werden beim Speichern abgelegt. */
  current: T
  /** Wird mit den gespeicherten Werten aufgerufen. */
  onApply: (params: T) => void
  /** Optional: verhindert das Speichern einer Ansicht ohne jeden Filter. */
  isEmpty?: (params: T) => boolean
}) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()
  const [saveOpen, setSaveOpen] = useState(false)
  const [form] = Form.useForm()

  const key = ['saved-views', pageKey]
  const listQ = useQuery<SavedView<T>[]>({
    queryKey: key,
    queryFn: async () => {
      const r = await apiClient.get(`/api/v1/saved-views?page=${encodeURIComponent(pageKey)}`)
      return r.data.data ?? []
    },
  })

  const save = useMutation({
    mutationFn: (v: { name: string; shared: boolean }) =>
      apiClient.post('/api/v1/saved-views', {
        page_key: pageKey, name: v.name, shared: v.shared, params: current,
      }),
    onSuccess: () => {
      message.success(t('savedViews.saved', 'Ansicht gespeichert'))
      setSaveOpen(false)
      qc.invalidateQueries({ queryKey: key })
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('common.saveFailed', 'Speichern fehlgeschlagen')),
  })

  const del = useMutation({
    mutationFn: (id: number) => apiClient.delete(`/api/v1/saved-views/${id}`),
    onSuccess: () => {
      message.success(t('savedViews.deleted', 'Ansicht gelöscht'))
      qc.invalidateQueries({ queryKey: key })
    },
    onError: () => message.error(t('common.error', 'Fehler')),
  })

  const views = listQ.data ?? []
  const nothingToSave = isEmpty ? isEmpty(current) : false

  return (
    <>
      <Space size={4} wrap>
        <Dropdown
          disabled={views.length === 0}
          menu={{
            items: views.map(v => ({
              key: String(v.id),
              label: (
                <Space size={6}>
                  <StarFilled style={{ color: '#faad14' }} />
                  <span>{v.name}</span>
                  {v.shared && (
                    <Tooltip title={v.own
                      ? t('savedViews.sharedOwn', 'Für das Team freigegeben')
                      : t('savedViews.sharedBy', { who: v.owner_email, defaultValue: `Geteilt von ${v.owner_email}` })}>
                      <TeamOutlined style={{ color: '#8c8c8c' }} />
                    </Tooltip>
                  )}
                </Space>
              ),
              onClick: () => onApply(v.params),
            })),
          }}
        >
          <Button size="small">
            {views.length === 0
              ? t('savedViews.none', 'Keine Ansichten')
              : t('savedViews.apply', 'Ansicht')} <DownOutlined />
          </Button>
        </Dropdown>

        <Tooltip title={nothingToSave
          ? t('savedViews.setFilterFirst', 'Erst filtern, dann als Ansicht speichern')
          : t('savedViews.saveCurrent', 'Aktuelle Filter als Ansicht speichern')}>
          <Button size="small" icon={<SaveOutlined />} disabled={nothingToSave}
            onClick={() => { form.resetFields(); setSaveOpen(true) }} />
        </Tooltip>

        {views.filter(v => v.own).length > 0 && (
          <Dropdown
            menu={{
              items: views.filter(v => v.own).map(v => ({
                key: String(v.id),
                label: (
                  <Popconfirm
                    title={t('savedViews.deleteConfirm', 'Ansicht löschen?')}
                    onConfirm={() => del.mutate(v.id)}
                  >
                    <Space size={6}><DeleteOutlined />{v.name}</Space>
                  </Popconfirm>
                ),
              })),
            }}
          >
            <Button size="small" icon={<DeleteOutlined />} />
          </Dropdown>
        )}
      </Space>

      <Modal
        open={saveOpen}
        title={t('savedViews.saveTitle', 'Ansicht speichern')}
        okText={t('common.save', 'Speichern')}
        confirmLoading={save.isPending}
        onCancel={() => setSaveOpen(false)}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ shared: false }}
          onFinish={(v) => save.mutate({ name: (v.name || '').trim(), shared: !!v.shared })}>
          <Form.Item name="name" label={t('savedViews.name', 'Name')}
            rules={[{ required: true, message: t('savedViews.nameRequired', 'Bitte einen Namen vergeben') }]}
            extra={t('savedViews.nameHint', 'Ein bereits vergebener Name überschreibt die vorhandene Ansicht.')}>
            <Input placeholder={t('savedViews.namePlaceholder', 'z. B. Überfällig & SEPA')} maxLength={80} />
          </Form.Item>
          <Form.Item name="shared" valuePropName="checked"
            label={t('savedViews.shared', 'Für das Team freigeben')}
            extra={t('savedViews.sharedHint', 'Andere sehen die Ansicht, ändern kannst nur du sie.')}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
